import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  Check,
  Pencil,
  Play,
  Share2,
  Sparkles,
  Timer,
} from 'lucide-react';
import { useAnalysis } from '../../hooks/useAnalysis';
import { useBeachvisionStore } from '../../store/beachvisionStore';
import { BeachvisionLayout } from '../../components/beachvision/BeachvisionLayout';
import { TakeawayCard } from '../../components/beachvision/TakeawayCard';
import { LazyFrameImage } from '../../components/beachvision/LazyFrameImage';
import { DebugDrawer } from '../../components/beachvision/DebugDrawer';
import { DebugPanel } from '../../components/beachvision/DebugPanel';
import { LoadingSkeleton } from '../../components/beachvision/LoadingSkeleton';
import { PageErrorBoundary } from '../../components/beachvision/PageErrorBoundary';
import { getAnalysisFrames } from '../../api/beachvision';
import {
  formatDate,
  formatReleaseMs,
  formatScore,
} from '../../lib/beachvision-utils';
import { isDebugModeEnabled } from '../../lib/debug-mode';
import { videoProcessingDebugItems } from '../../utils/videoDebug';

/**
 * Full analysis view with metrics and takeaway list (design-aligned layout).
 */
function formatPointDurationSeconds(startMs, endMs) {
  if (startMs == null || endMs == null) return null;
  const start = Number(startMs);
  const end = Number(endMs);
  if (Number.isNaN(start) || Number.isNaN(end)) return null;
  const seconds = Math.max(0, (end - start) / 1000);
  return `${seconds.toFixed(1)}s`;
}

const pointValIcon = 'h-3.5 w-3.5 sm:h-4 sm:w-4';

/** Points list: validation at a glance (list tab only; detail page has full controls). */
function PointValidationListIcon({ point, compact = false }) {
  const ico = compact ? 'h-3.5 w-3.5' : pointValIcon;
  if (point?.processing_status !== 'complete') return null;
  const status = point?.validation_status;
  const runs = Number(point?.validation_run_count);
  const runCount = Number.isFinite(runs) ? runs : 0;
  if (status === 'failed' || status === 'warn') {
    return (
      <span
        className="inline-flex h-5 shrink-0 items-center justify-center"
        title="Validation flagged a possible issue with this point"
        aria-label="Validation needs attention"
      >
        <AlertTriangle
          className={`${ico} block text-amber-500/85`}
          strokeWidth={2.25}
          aria-hidden
        />
      </span>
    );
  }
  if (status === 'pass') {
    if (runCount >= 2) {
      return (
        <span
          className="inline-flex h-5 shrink-0 items-center justify-center"
          title="Valid now, after a follow-up re-check"
          aria-label="Re-validated after a follow-up"
        >
          <Pencil
            className={`${ico} block text-beachvision-muted/50`}
            strokeWidth={2.1}
            aria-hidden
          />
        </span>
      );
    }
    return (
      <span
        className="inline-flex h-5 shrink-0 items-center justify-center"
        title="Point validation passed"
        aria-label="Validated"
      >
        <Check
          className={`${ico} block text-beachvision-muted/40`}
          strokeWidth={2.25}
          aria-hidden
        />
      </span>
    );
  }
  return null;
}

/** More linked points/clips first; stable tie-breaker by model order. */
function sortTakeawaysByClipCountDesc(items) {
  return [...items].sort((x, y) => {
    const cx = x.point_clips?.length ?? 0;
    const cy = y.point_clips?.length ?? 0;
    if (cy !== cx) return cy - cx;
    return (x.order ?? 0) - (y.order ?? 0);
  });
}

function PointFrameCarousel({ point, pointFramesByPointId, linkFramesByClipId }) {
  const scrollerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const frames = useMemo(() => {
    const builtFrames = [
      {
      key: `point-start-${point.id}`,
      timestampMs: point.timestamp_start_ms,
      label: 'Point start',
      kind: 'point_start',
      frameImageUrl: pointFramesByPointId[String(point.id)] || null,
      },
    ];
    (point.takeaway_frames || []).forEach((frame, idx) => {
      builtFrames.push({
        key: `takeaway-${point.id}-${frame.takeaway_id || idx}`,
        label: frame.takeaway_title || 'Takeaway moment',
        kind: 'takeaway',
        frameImageUrl: linkFramesByClipId[String(frame.clip_id)] || null,
      });
    });
    return builtFrames;
  }, [point, pointFramesByPointId, linkFramesByClipId]);

  const handleScroll = () => {
    const node = scrollerRef.current;
    if (!node) return;
    const slideWidth = node.clientWidth || 1;
    const idx = Math.round(node.scrollLeft / slideWidth);
    setActiveIndex(Math.max(0, Math.min(idx, frames.length - 1)));
  };

  return (
    <div className="w-24 shrink-0">
      <div
        ref={scrollerRef}
        onScroll={handleScroll}
        className="w-24 h-28 rounded-lg overflow-x-auto overflow-y-hidden flex snap-x snap-mandatory scroll-smooth [touch-action:pan-x] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {frames.map((frame) => (
          <div key={frame.key} className="relative w-24 h-28 shrink-0 snap-start">
            <LazyFrameImage
              className="absolute inset-0 h-full w-full"
              frameRequestKey={frame.key}
              loadImage={() => Promise.resolve(frame.frameImageUrl)}
              frameUnavailable={!frame.frameImageUrl}
              alt={`${frame.label} for point ${point.order}`}
            />
            {frame.kind === 'point_start' ? (
              <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="rounded-full bg-black/55 p-2 backdrop-blur-sm">
                  <Play className="h-4 w-4 text-white fill-white/90" />
                </span>
              </span>
            ) : null}
          </div>
        ))}
      </div>
      {frames.length > 1 ? (
        <div className="mt-2 flex items-center justify-center gap-1.5">
          {frames.map((frame, idx) => (
            <span
              key={`dot-${frame.key}`}
              className={`h-1.5 rounded-full transition-all ${
                idx === activeIndex ? 'w-4 bg-white/90' : 'w-1.5 bg-white/35'
              }`}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function AnalysisResultsInner({ currentUser }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const setSessionAnalysis = useBeachvisionStore((s) => s.setSessionAnalysis);
  const { data: analysisRes, isLoading, isError, refetch, error } = useAnalysis(
    id,
    { refetchWhileProcessingMs: 4000 }
  );
  const location = useLocation();
  const activeTab = useMemo(() => {
    const path = location.pathname || '';
    if (path.endsWith('/points/')) return 'points';
    if (path.endsWith('/strengths/')) return 'strengths';
    return 'takeaways';
  }, [location.pathname]);
  const debugModeEnabled = isDebugModeEnabled(currentUser);
  const [pointFramesByPointId, setPointFramesByPointId] = useState({});
  const [linkFramesByClipId, setLinkFramesByClipId] = useState({});

  const a = analysisRes?.data;
  const takeaways = useMemo(() => [...(a?.takeaways || [])], [a]);
  const needsWorkTakeaways = useMemo(
    () =>
      sortTakeawaysByClipCountDesc(
        takeaways.filter((t) => t.category === 'needs_work')
      ),
    [takeaways]
  );
  const strengthTakeaways = useMemo(
    () =>
      sortTakeawaysByClipCountDesc(
        takeaways.filter((t) => t.category === 'strength')
      ),
    [takeaways]
  );
  const points = useMemo(
    () => [...(a?.points || [])].sort((x, y) => x.order - y.order),
    [a]
  );
  const debugItems = useMemo(() => {
    if (!a) return [];
    const rawGeminiResponse = a?.raw_gemini_response || {};
    const videoDebugRows = videoProcessingDebugItems(rawGeminiResponse?._meta?.video_debug);
    const intake = rawGeminiResponse?.intake || {};
    const intakePayload = intake?.payload || {};
    const playerDetectCount = Array.isArray(intakePayload.player_detections)
      ? intakePayload.player_detections.length
      : null;
    const intakePassedGate =
      typeof intakePayload.is_beach_volleyball === 'boolean' &&
      typeof intakePayload.beach_volleyball_confidence === 'number'
        ? String(
            intakePayload.is_beach_volleyball &&
              intakePayload.beach_volleyball_confidence >= 0.7
          )
        : 'unknown';
    const failedPoints = points.filter((p) => p.processing_status === 'failed').length;
    const rawKeys = Object.keys(rawGeminiResponse);
    const rawSizeChars = JSON.stringify(rawGeminiResponse || {}).length;
    return [
      { label: 'Analysis ID', value: String(a?.id || '-') },
      { label: 'Generated at', value: String(a?.generated_at || '-') },
      ...videoDebugRows,
      { label: 'Pipeline stage', value: String(a?.stage || '-') },
      { label: 'Pipeline progress', value: `${a?.progress_pct ?? '-'}%` },
      { label: 'Intake model', value: String(intake?.model || 'unknown') },
      {
        label: 'Quality gate (derived)',
        value: intakePassedGate,
      },
      {
        label: 'our_team side at video start',
        value: String(intakePayload?.our_team_side_at_video_start || '-'),
      },
      {
        label: 'Player detect summary',
        value: `${playerDetectCount ?? '-'} players`,
      },
      {
        label: 'Point processing',
        value: `${points.length - failedPoints}/${points.length} complete`,
      },
      { label: 'Takeaway count', value: String(takeaways.length) },
      { label: 'Raw response size', value: `${rawSizeChars} chars` },
      { label: 'Raw response keys', value: rawKeys.slice(0, 4).join(', ') || 'none' },
    ];
  }, [a, points, takeaways.length]);

  useEffect(() => {
    if (analysisRes?.ok && analysisRes.data) {
      setSessionAnalysis(id, analysisRes.data);
    }
  }, [analysisRes, id, setSessionAnalysis]);

  useEffect(() => {
    if (!analysisRes?.ok || !analysisRes?.data?.id) return;
    let cancelled = false;
    getAnalysisFrames(analysisRes.data.id)
      .then((payload) => {
        if (cancelled) return;
        const pointMap = {};
        const linkMap = {};
        (payload?.point_frames || []).forEach((row) => {
          if (row?.point_id && row?.frame_url) {
            pointMap[String(row.point_id)] = row.frame_url;
          }
        });
        (payload?.takeaway_point_frames || []).forEach((row) => {
          if (row?.clip_id && row?.frame_url) {
            linkMap[String(row.clip_id)] = row.frame_url;
          }
        });
        setPointFramesByPointId(pointMap);
        setLinkFramesByClipId(linkMap);
      })
      .catch(() => {
        if (!cancelled) {
          setPointFramesByPointId({});
          setLinkFramesByClipId({});
        }
      });
    return () => { cancelled = true; };
  }, [analysisRes?.ok, analysisRes?.data?.id]);

  useEffect(() => {
    if (analysisRes?.ok === false && analysisRes?.processing) {
      navigate('/dashboard/', { replace: true });
    }
  }, [analysisRes, id, navigate]);

  if (isLoading) {
    return (
      <div className="px-4 pt-6 space-y-4">
        <LoadingSkeleton className="h-8 w-40" />
        <div className="grid grid-cols-2 gap-3">
          <LoadingSkeleton className="h-28" />
          <LoadingSkeleton className="h-28" />
        </div>
        <LoadingSkeleton className="h-20 w-full" />
        <LoadingSkeleton className="h-20 w-full" />
      </div>
    );
  }

  if (isError || !analysisRes?.ok) {
    const retryAfter = error?.response?.data?.retry_after;
    return (
      <div className="px-4 py-16 text-center">
        <p className="text-beachvision-muted mb-4">
          {error?.response?.data?.error || 'Analysis is not ready.'}
        </p>
        {retryAfter ? (
          <p className="text-xs text-beachvision-muted mb-4">Retry after {retryAfter}s</p>
        ) : null}
        <button
          type="button"
          onClick={() => refetch()}
          className="w-full max-w-xs mx-auto bg-beachvision-cta text-beachvision-on-coral rounded-xl px-6 py-3 font-display font-bold"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!a) return null;

  const share = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      toast.success('Link copied to clipboard');
    });
  };

  const dateStr = a?.session_recorded_at
    ? formatDate(a.session_recorded_at).toUpperCase()
    : '';

  return (
    <>
      <header className="px-4 pt-6 flex items-start gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Link
            to="/dashboard/"
            className="p-2.5 rounded-xl bg-beachvision-surface-low shrink-0"
            aria-label="Back to dashboard"
          >
            <ArrowLeft className="h-5 w-5 text-white" />
          </Link>
          <div className="min-w-0">
            <p className="font-display font-black text-lg text-white tracking-tight">
              Sand<span className="text-beachvision-primary-light">IQ</span>
            </p>
            <p className="text-xs text-beachvision-muted mt-0.5 truncate">
              <span className="text-beachvision-coral font-semibold uppercase tracking-wide">
                Analysis results
              </span>
              {dateStr ? (
                <>
                  <span className="text-beachvision-muted"> · </span>
                  {dateStr}
                </>
              ) : null}
            </p>
          </div>
        </div>
      </header>

      <div className="px-4 mt-6 grid grid-cols-2 gap-3">
        <div className="relative rounded-2xl bg-beachvision-surface-low p-4 text-center overflow-hidden">
          <BarChart3
            className="absolute top-3 right-3 h-5 w-5 text-beachvision-coral/50"
            aria-hidden
          />
          <p className="text-3xl font-display font-black text-beachvision-teal">
            {formatScore(a.form_consistency_pct)}
          </p>
          <p className="text-xs text-beachvision-muted mt-2 font-display font-bold uppercase tracking-wide">
            Form consistency
          </p>
        </div>
        <div className="relative rounded-2xl bg-beachvision-surface-low p-4 text-center overflow-hidden">
          <Timer
            className="absolute top-3 right-3 h-5 w-5 text-beachvision-coral/50"
            aria-hidden
          />
          <p className="text-3xl font-display font-black text-beachvision-primary-light">
            {formatReleaseMs(a.avg_release_time_ms)}
          </p>
          <p className="text-xs text-beachvision-muted mt-2 font-display font-bold uppercase tracking-wide">
            Avg release time
          </p>
        </div>
      </div>

      <section
        className={`px-4 mt-8${takeaways.length === 0 ? ' pb-4' : ''}`}
      >
        <div className="grid grid-cols-3 gap-1.5 rounded-xl bg-beachvision-surface-low p-1.5">
          <button
            type="button"
            className={`rounded-lg py-2 px-1 text-xs sm:text-sm font-display font-bold ${
              activeTab === 'takeaways'
                ? 'bg-beachvision-coral text-beachvision-on-coral'
                : 'text-beachvision-muted'
            }`}
            onClick={() => navigate(`/analysis/${id}/takeaways/`)}
          >
            Takeaways
          </button>
          <button
            type="button"
            className={`rounded-lg py-2 px-1 text-xs sm:text-sm font-display font-bold ${
              activeTab === 'strengths'
                ? 'bg-beachvision-coral text-beachvision-on-coral'
                : 'text-beachvision-muted'
            }`}
            onClick={() => navigate(`/analysis/${id}/strengths/`)}
          >
            Strengths
          </button>
          <button
            type="button"
            className={`rounded-lg py-2 px-1 text-xs sm:text-sm font-display font-bold ${
              activeTab === 'points'
                ? 'bg-beachvision-coral text-beachvision-on-coral'
                : 'text-beachvision-muted'
            }`}
            onClick={() => navigate(`/analysis/${id}/points/`)}
          >
            Points
          </button>
        </div>
        {activeTab === 'takeaways' ? (
          <div className="mt-5 space-y-4">
            {needsWorkTakeaways.length === 0 ? (
              <div className="rounded-2xl bg-beachvision-surface-low border border-white/5 p-8 text-center">
                <div className="text-5xl mb-3" aria-hidden>
                  🏐
                </div>
                <p className="text-white font-display font-semibold mb-1">
                  No takeaways for this session
                </p>
                <p className="text-beachvision-muted text-sm">
                  Use Points to review each rally, check Strengths for positives, or check back if analysis is still running.
                </p>
              </div>
            ) : (
              needsWorkTakeaways.map((t) => (
                <TakeawayCard
                  key={t.id}
                  takeaway={t}
                  linkFramesByClipId={linkFramesByClipId}
                  to={`/analysis/${id}/takeaways/${t.id}/`}
                />
              ))
            )}
          </div>
        ) : activeTab === 'strengths' ? (
          <div className="mt-5 space-y-4">
            {strengthTakeaways.length === 0 ? (
              <div className="rounded-2xl bg-beachvision-surface-low border border-white/5 p-8 text-center">
                <div className="text-5xl mb-3" aria-hidden>
                  🏐
                </div>
                <p className="text-white font-display font-semibold mb-1">
                  No strengths flagged yet
                </p>
                <p className="text-beachvision-muted text-sm">
                  When the model highlights what went well, it will show up here.
                </p>
              </div>
            ) : (
              strengthTakeaways.map((t) => (
                <TakeawayCard
                  key={t.id}
                  takeaway={t}
                  linkFramesByClipId={linkFramesByClipId}
                  to={`/analysis/${id}/strengths/${t.id}/`}
                />
              ))
            )}
          </div>
        ) : (
          <div className="mt-5 space-y-3">
            {points.map((p) => {
              const pointDuration = formatPointDurationSeconds(
                p.timestamp_start_ms,
                p.timestamp_end_ms
              );
              const pointNumber = p.order ?? '-';
              return (
                <Link
                  key={p.id}
                  to={`/analysis/${id}/points/${p.id}/`}
                  className="block w-full text-left rounded-xl bg-beachvision-surface-low border border-white/5 p-4 hover:brightness-110 transition-[filter]"
                >
                  <div className="flex items-stretch gap-3">
                    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                      <div className="min-w-0 space-y-2">
                        <p className="font-display font-bold leading-snug text-white">
                          <span className="mr-1 text-white/50">#{pointNumber}</span>
                          {p.point_title || `Point ${pointNumber}`}
                        </p>
                        <div className="flex min-w-0 flex-wrap content-start gap-1.5">
                          <span
                            className={`shrink-0 text-[10px] sm:text-[11px] px-2 py-1 rounded-full font-display font-bold uppercase tracking-wide ${
                              p.winner === 'our_team'
                                ? 'bg-beachvision-teal/25 text-beachvision-teal'
                                : p.winner === 'opponent'
                                  ? 'bg-beachvision-coral/20 text-beachvision-coral'
                                  : 'bg-white/8 text-beachvision-muted/90'
                            }`}
                          >
                            {p.winner === 'our_team'
                              ? 'Won'
                              : p.winner === 'opponent'
                                ? 'Lost'
                                : 'Unknown'}
                          </span>
                          {(p.takeaway_tags || []).map((tag) => (
                            <span
                              key={`${p.id}-${tag.takeaway_id}`}
                              className={`shrink-0 whitespace-nowrap text-[10px] sm:text-[11px] px-2 py-1 rounded-full font-display font-bold uppercase tracking-wide ${
                                tag.category === 'strength' || tag.score >= 60
                                  ? 'bg-beachvision-teal/20 text-beachvision-teal'
                                  : 'bg-beachvision-coral/20 text-beachvision-coral'
                              }`}
                            >
                              {tag.title}
                            </span>
                          ))}
                        </div>
                        {p.processing_status === 'failed' ? (
                          <p className="text-xs text-beachvision-coral font-display font-bold uppercase tracking-wide">
                            Could not be processed
                          </p>
                        ) : null}
                      </div>
                      <div
                        className="mt-auto flex w-full min-w-0 items-center justify-between gap-3 pt-2 text-[11px] sm:text-xs leading-5 text-beachvision-muted/80"
                        aria-label="Point stats"
                      >
                        <div className="flex min-h-5 min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1 sm:gap-x-2.5">
                          <span className="inline-flex min-h-5 items-center font-medium tabular-nums">
                            {p.score_label || `${p.score_for ?? '-'}-${p.score_against ?? '-'}`}
                          </span>
                          {pointDuration ? (
                            <>
                              <span
                                className="inline-flex h-5 select-none items-center text-[10px] leading-5 text-beachvision-muted/30"
                                aria-hidden
                              >
                                ·
                              </span>
                              <span className="inline-flex min-h-5 items-center gap-1.5 tabular-nums">
                                <Timer
                                  className="h-3.5 w-3.5 shrink-0 text-beachvision-muted/50 block"
                                  strokeWidth={2}
                                  aria-hidden
                                />
                                {pointDuration}
                              </span>
                            </>
                          ) : null}
                        </div>
                        <PointValidationListIcon point={p} compact />
                      </div>
                    </div>
                    <PointFrameCarousel
                      point={p}
                      pointFramesByPointId={pointFramesByPointId}
                      linkFramesByClipId={linkFramesByClipId}
                    />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      {takeaways.length > 0 ? (
        <div className="px-4 mt-10 space-y-3 pb-4">
          <button
            type="button"
            className="w-full bg-beachvision-cta text-beachvision-on-coral rounded-xl px-6 py-3.5 font-display font-bold flex items-center justify-center gap-2 opacity-85"
            onClick={() => toast('Coming soon', { icon: '🏋️' })}
          >
            <span>Generate training program</span>
            <Sparkles className="h-5 w-5" strokeWidth={2.5} />
          </button>
          <button
            type="button"
            onClick={share}
            className="w-full bg-beachvision-surface-low text-white rounded-xl px-6 py-3.5 font-display font-bold flex items-center justify-center gap-2"
          >
            <Share2 className="h-5 w-5 text-beachvision-muted" />
            Share with coach
          </button>
        </div>
      ) : null}

      <DebugDrawer enabled={debugModeEnabled}>
        <DebugPanel title="Admin Debug" items={debugItems} />
      </DebugDrawer>
    </>
  );
}

export default function AnalysisResultsPage(props) {
  return (
    <BeachvisionLayout>
      <PageErrorBoundary>
        <AnalysisResultsInner {...props} />
      </PageErrorBoundary>
    </BeachvisionLayout>
  );
}
